import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {StepTransitionStatusType} from 'notebook/worksheets/steps/WorksheetStepButton';
import {WorksheetTaskStatusType} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetViewQuery} from 'notebook/worksheets/workspace/__generated/WorksheetViewQuery';
import {PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask as PMEIMethodTask} from 'procedures/worksheets/__generated/PMEIsQuery';
import {TransitionStatusType} from 'procedures/worksheets/TransitionRunModal';

import {processTimingEvent} from './BenchlingProfiler';
import {toDatadogTagFormat, getBucketStringForTag} from './PerformanceUtils';
import {EventMetadata, NOT_SPECIFIED, ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_WORKSHEET_EVENTS_VERSION = 8;

export enum ProfilerWorksheetsFeatures {
  WORKSHEET_TRANSITION_RUN_STATUS = 'worksheetTransitionRunStatus',
  WORKSHEET_STEP_TRANSITION_STATUS = 'worksheetStepTransitionStatus',
  WORKSHEET_INITIAL_LOAD = 'worksheetInitialLoad',
  WORKSHEET_STEP_COMPLETION = 'worksheetStepCompletion',
  WORKSHEET_REORDER_STEP = 'worksheetReorderStep',
  WORKSHEET_ADD_STEP = 'worksheetAddStep',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerWorksheetsFeatures]: Function} = {
  [ProfilerWorksheetsFeatures.WORKSHEET_TRANSITION_RUN_STATUS]: processWorksheetTransitionRunStatus,
  [ProfilerWorksheetsFeatures.WORKSHEET_STEP_TRANSITION_STATUS]: processWorksheetStepTransitionStatus,
  [ProfilerWorksheetsFeatures.WORKSHEET_INITIAL_LOAD]: processWorksheetInitialLoad,
  [ProfilerWorksheetsFeatures.WORKSHEET_STEP_COMPLETION]: processWorksheetStepCompletion,
  [ProfilerWorksheetsFeatures.WORKSHEET_REORDER_STEP]: processWorksheetReorderStep,
  [ProfilerWorksheetsFeatures.WORKSHEET_ADD_STEP]: processWorksheetAddStep,
};

enum ProfilerWorksheetActions {
  WORKSHEET_TRANSITION_RUN_STATUS_STARTED = 'worksheetTransitionRunStatusStarted',
  WORKSHEET_TRANSITION_RUN_STATUS_ENDED = 'worksheetTransitionRunStatusEnded',
  WORKSHEET_STEP_TRANSITION_STATUS_STARTED = 'worksheetStepTransitionStatusStarted',
  WORKSHEET_STEP_TRANSITION_STATUS_ENDED = 'worksheetStepTransitionStatusEnded',
  WORKSHEET_INITIAL_LOAD_STARTED = 'worksheetInitialLoadStarted',
  WORKSHEET_INITIAL_LOAD_ENDED = 'worksheetIntialLoadEnded',
  WORKSHEET_STEP_COMPLETION_STARTED = 'worksheetStepCompletionStarted',
  WORKSHEET_STEP_COMPLETION_ENDED = 'worksheetStepCompletionEnded',
  WORKSHEET_REORDER_STEP_STARTED = 'worksheetReorderStepStarted',
  WORKSHEET_REORDER_STEP_ENDED = 'worksheetReorderStepEnded',
  WORKSHEET_ADD_STEP_STARTED = 'worksheetAddStepStarted',
  WORKSHEET_ADD_STEP_ENDED = 'worksheetAddStepEnded',
}

export const WORKSHEET_TRANSITION_RUN_STATUS_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_TRANSITION_RUN_STATUS,
  action: ProfilerWorksheetActions.WORKSHEET_TRANSITION_RUN_STATUS_STARTED,
};
export const WORKSHEET_TRANSITION_RUN_STATUS_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_TRANSITION_RUN_STATUS,
  action: ProfilerWorksheetActions.WORKSHEET_TRANSITION_RUN_STATUS_ENDED,
};

export const WORKSHEET_STEP_TRANSITION_STATUS_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_STEP_TRANSITION_STATUS,
  action: ProfilerWorksheetActions.WORKSHEET_STEP_TRANSITION_STATUS_STARTED,
};
export const WORKSHEET_STEP_TRANSITION_STATUS_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_STEP_TRANSITION_STATUS,
  action: ProfilerWorksheetActions.WORKSHEET_STEP_TRANSITION_STATUS_ENDED,
};

export const WORKSHEET_INITIAL_LOAD_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_INITIAL_LOAD,
  action: ProfilerWorksheetActions.WORKSHEET_INITIAL_LOAD_STARTED,
};
export const WORKSHEET_INITIAL_LOAD_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_INITIAL_LOAD,
  action: ProfilerWorksheetActions.WORKSHEET_INITIAL_LOAD_ENDED,
};

export const WORKSHEET_STEP_COMPLETION_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_STEP_COMPLETION,
  action: ProfilerWorksheetActions.WORKSHEET_STEP_COMPLETION_STARTED,
};
export const WORKSHEET_STEP_COMPLETION_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_STEP_COMPLETION,
  action: ProfilerWorksheetActions.WORKSHEET_STEP_COMPLETION_ENDED,
};
export const WORKSHEET_REORDER_STEP_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_REORDER_STEP,
  action: ProfilerWorksheetActions.WORKSHEET_REORDER_STEP_STARTED,
};
export const WORKSHEET_REORDER_STEP_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_REORDER_STEP,
  action: ProfilerWorksheetActions.WORKSHEET_REORDER_STEP_ENDED,
};
export const WORKSHEET_ADD_STEP_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_ADD_STEP,
  action: ProfilerWorksheetActions.WORKSHEET_ADD_STEP_STARTED,
};
export const WORKSHEET_ADD_STEP_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_ADD_STEP,
  action: ProfilerWorksheetActions.WORKSHEET_ADD_STEP_ENDED,
};

/**
 * Mapping from a worksheet run transition status type to action name.
 * Uses a Record to ensure any changes to the TransitionStatusType enum are reflected here.
 */
const transitionRunStatusTypeToActionName: Record<TransitionStatusType, string> = {
  [WorksheetTaskStatusType.IN_PROGRESS]: 'start_run',
  [WorksheetTaskStatusType.COMPLETED]: 'complete_run',
  [WorksheetTaskStatusType.FAILED]: 'fail_run',
  [WorksheetTaskStatusType.SKIPPED]: 'skip_run',
};
/**
 * Mapping from a worksheet step transition status type to action name.
 * Uses a Record to ensure any changes to the TransitionStatusType enum are reflected here.
 */
const transitionStepStatusTypeToActionName: Record<StepTransitionStatusType, string> = {
  [WorksheetTaskStatusType.READY]: 'amend_step',
  [WorksheetTaskStatusType.COMPLETED]: 'complete_step',
  [WorksheetTaskStatusType.FAILED]: 'fail_step',
  [WorksheetTaskStatusType.SKIPPED]: 'skip_step',
};

/**
 * Mapping from a procedure type to the type name in the dashboard.
 * Uses a Record to ensure any changes to the ProcedureType enum are reflected here.
 */
export const procedureTypeToName: Record<ProcedureType, string> = {
  [ProcedureType.ASSAY]: 'assay',
  [ProcedureType.RECIPE]: 'recipe',
};

/**
 * Build metadata for worksheet transition run status event
 */
export function composeWorksheetTransitionRunEventMetadata(
  tasks: Array<PMEIMethodTask>,
  transitionToStatus: TransitionStatusType,
  isPow: boolean,
  transitionFromStatus?: WorksheetTaskStatusType,
  procedureType?: ProcedureType
): EventMetadata {
  return {
    distributionMetadata: {
      action: transitionRunStatusTypeToActionName[transitionToStatus],
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
      num_tasks: tasks.length
        ? getBucketStringForTag(tasks.length, BenchlingConfig.PROCEDURES_PERF_MONITORING_NUM_TASKS_BUCKETS)
        : NOT_SPECIFIED,
      is_pow: isPow,
    },
    actionMetadata: {
      transition_from_status: toDatadogTagFormat(transitionFromStatus ?? NOT_SPECIFIED),
      transition_to_status: toDatadogTagFormat(transitionToStatus),
      action: transitionRunStatusTypeToActionName[transitionToStatus],
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
      num_tasks: tasks.length
        ? getBucketStringForTag(tasks.length, BenchlingConfig.PROCEDURES_PERF_MONITORING_NUM_TASKS_BUCKETS)
        : NOT_SPECIFIED,
      is_pow: isPow,
    },
  };
}

/**
 * Build metadata for worksheet step transition status event
 */
export function composeWorksheetStepTransitionEventMetadata(
  transitionToStatus: StepTransitionStatusType,
  isPow: boolean,
  transitionFromStatus?: WorksheetTaskStatusType,
  stepTaskIds?: Array<string>,
  procedureType?: ProcedureType
): EventMetadata {
  return {
    distributionMetadata: {
      action: transitionStepStatusTypeToActionName[transitionToStatus],
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
      num_tasks:
        stepTaskIds != null
          ? getBucketStringForTag(
              stepTaskIds.length,
              BenchlingConfig.PROCEDURES_PERF_MONITORING_NUM_TASKS_BUCKETS
            )
          : NOT_SPECIFIED,
      is_pow: isPow,
    },
    actionMetadata: {
      transition_from_status: toDatadogTagFormat(transitionFromStatus ?? NOT_SPECIFIED),
      transition_to_status: toDatadogTagFormat(transitionToStatus),
      action: transitionStepStatusTypeToActionName[transitionToStatus],
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
      num_tasks:
        stepTaskIds != null
          ? getBucketStringForTag(
              stepTaskIds.length,
              BenchlingConfig.PROCEDURES_PERF_MONITORING_NUM_TASKS_BUCKETS
            )
          : NOT_SPECIFIED,
      is_pow: isPow,
    },
  };
}

/**
 * Build metadata for worksheet step addition or re-order event
 */
export function composeWorksheetAddOrReorderStepEventMetadata(
  tasks: Array<PMEIMethodTask>,
  procedureType?: ProcedureType
): EventMetadata {
  return {
    distributionMetadata: {
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
      num_tasks: tasks.length
        ? getBucketStringForTag(tasks.length, BenchlingConfig.PROCEDURES_PERF_MONITORING_NUM_TASKS_BUCKETS)
        : NOT_SPECIFIED,
    },
    actionMetadata: {
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
      num_tasks: tasks.length
        ? getBucketStringForTag(tasks.length, BenchlingConfig.PROCEDURES_PERF_MONITORING_NUM_TASKS_BUCKETS)
        : NOT_SPECIFIED,
    },
  };
}

/**
 * Build metadata for worksheet initial load event
 */
export function composeWorksheetInitialLoadEventMetadata(
  worksheet: WorksheetViewQuery['worksheet'],
  isPow: boolean,
  procedureType?: ProcedureType
): EventMetadata {
  let worksheetType = NOT_SPECIFIED;
  let stepCount = 0;
  worksheetType = procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED;
  if (!isInaccessible(worksheet)) {
    stepCount = worksheet?.worksheetStepGroups.flatMap((stepGroup) => stepGroup.worksheetSteps).length ?? 0;
  }

  return {
    distributionMetadata: {
      worksheet_type: worksheetType,
      is_pow: isPow,
    },
    actionMetadata: {
      worksheet_type: worksheetType,
      step_count: stepCount,
      is_pow: isPow,
    },
  };
}

/**
 * Build metadata for worksheet step completion event
 */
export function composeWorksheetStepCompletionEventMetadata(procedureType?: ProcedureType): EventMetadata {
  const worksheetType = procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED;

  return {
    distributionMetadata: {
      worksheet_type: worksheetType,
    },
    actionMetadata: {
      worksheet_type: worksheetType,
    },
  };
}

export function processWorksheetsEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerWorksheetsFeatures;
  assert(
    Object.values(ProfilerWorksheetsFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

/**
 * Process a Worksheet Transition Run Status event
 */
function processWorksheetTransitionRunStatus(): void {
  processTimingEvent(
    WORKSHEET_TRANSITION_RUN_STATUS_STARTED_EVENT,
    WORKSHEET_TRANSITION_RUN_STATUS_ENDED_EVENT,
    'Worksheet Transition Run Status',
    'perf.worksheet_transition_run_status',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}

/**
 * Process a Worksheet Step Transition Status event
 */
function processWorksheetStepTransitionStatus(): void {
  processTimingEvent(
    WORKSHEET_STEP_TRANSITION_STATUS_STARTED_EVENT,
    WORKSHEET_STEP_TRANSITION_STATUS_ENDED_EVENT,
    'Worksheet Step Transition Status',
    'perf.worksheet_step_transition_status',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}

/**
 * Process a Worksheet Initial Load event
 */
function processWorksheetInitialLoad(): void {
  processTimingEvent(
    WORKSHEET_INITIAL_LOAD_STARTED_EVENT,
    WORKSHEET_INITIAL_LOAD_ENDED_EVENT,
    'Worksheet Initial Load',
    'perf.worksheet_initial_load',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}

/**
 * Process a Worksheet Step Completion event
 */
function processWorksheetStepCompletion(): void {
  processTimingEvent(
    WORKSHEET_STEP_COMPLETION_STARTED_EVENT,
    WORKSHEET_STEP_COMPLETION_ENDED_EVENT,
    'Worksheet Step Completion',
    'perf.worksheet_step_completion',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}

/**
 * Process a Worksheet Reorder Step event
 */
function processWorksheetReorderStep(): void {
  processTimingEvent(
    WORKSHEET_REORDER_STEP_STARTED_EVENT,
    WORKSHEET_REORDER_STEP_ENDED_EVENT,
    'Worksheet Reorder Step',
    'perf.worksheet_reorder_step',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}

/**
 * Process a Worksheet Add Step event
 */
function processWorksheetAddStep(): void {
  processTimingEvent(
    WORKSHEET_ADD_STEP_STARTED_EVENT,
    WORKSHEET_ADD_STEP_ENDED_EVENT,
    'Worksheet Add Step',
    'perf.worksheet_add_step',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}
